import type { PowerPathOverviewTab } from '@ps/web-martech-cms-types';
import classNames from 'clsx';
import React, { useMemo } from 'react';

import { CMSBrightcoveVideo, CMSCTA, useCMSReference } from '@swf/cms';

import Arrow from '../../assets/icons/arrow.svg';
import { usePokerPowerPathWidget } from '../../model';
import { getOverviewSections } from '../../utils';

import styles from './Overview.scss';
import HeadingWithAccentedText from './components/HeadingWithAccentedText/HeadingWithAccentedText';
import SimpleTile from './components/Tile/SimpleTile';
import StoryTile from './components/Tile/StoryTile';
import Tile from './components/Tile/Tile';

const Overview = () => {
  const { widgetConfig } = usePokerPowerPathWidget();
  const overviewTab = useCMSReference<PowerPathOverviewTab>(widgetConfig.overview_tab[0]);
  const overviewSections = useMemo(() => getOverviewSections(overviewTab), [overviewTab]);

  if (!overviewSections) return;

  const {
    tilesWithImageAndDescription,
    howItWorksVideo,
    playForFreeDescription,
    powerPathPrizes,
    winnersStories,
    latestPosts
  } = overviewSections;

  return (
    <div className={styles.root}>
      {tilesWithImageAndDescription.visibility.root && (
        <section
          className={styles.tilesWithImageAndDescription}
          style={{
            backgroundColor: tilesWithImageAndDescription.data.background_color
          }}
        >
          <div className={styles.content}>
            {tilesWithImageAndDescription.data.simple_tiles.map((tile, index) => (
              <React.Fragment key={index}>
                <SimpleTile {...tile} />
                {index < tilesWithImageAndDescription.data.simple_tiles.length - 1 && (
                  <Arrow className={classNames(styles.arrow)} />
                )}
              </React.Fragment>
            ))}
          </div>
        </section>
      )}

      {playForFreeDescription.visibility.root && (
        <section
          className={styles.playForFreeDescription}
          style={{
            backgroundColor: playForFreeDescription.data.background_color
          }}
        >
          <HeadingWithAccentedText
            className={styles.heading}
            {...playForFreeDescription.data.heading}
          />
          {playForFreeDescription.data.description && (
            <p className={styles.description}>{playForFreeDescription.data.description}</p>
          )}
          <CMSCTA className={styles.cta} {...playForFreeDescription.data.cta} />
        </section>
      )}

      {howItWorksVideo.visibility.root && (
        <section
          className={styles.howItWorksVideo}
          style={{
            backgroundColor: howItWorksVideo.data.background_color
          }}
        >
          <HeadingWithAccentedText className={styles.heading} {...howItWorksVideo.data.heading} />
          {howItWorksVideo.visibility.video && (
            <div className={styles.videoContainer}>
              <CMSBrightcoveVideo
                video_id={howItWorksVideo.data.brightcove_video.brightcove_video_id}
                player_id={howItWorksVideo.data.brightcove_video.brightcove_player_id}
              />
            </div>
          )}
        </section>
      )}

      {powerPathPrizes.visibility.root && (
        <section
          className={styles.powerPathPrizes}
          style={{
            backgroundColor: powerPathPrizes.data.background_color
          }}
        >
          <HeadingWithAccentedText className={styles.heading} {...powerPathPrizes.data.heading} />
          <div className={styles.content}>
            {powerPathPrizes.data.prizes_tiles.map((tile, index) => (
              <Tile
                key={index}
                classes={{
                  text: styles.centered
                }}
                image={tile.image}
                heading={tile.heading}
                text={tile.description}
                cta={tile.cta}
              />
            ))}
          </div>
        </section>
      )}

      {winnersStories.visibility.root && (
        <section
          className={styles.winnersStories}
          style={{
            backgroundColor: winnersStories.data.background_color
          }}
        >
          <HeadingWithAccentedText className={styles.heading} {...winnersStories.data.heading} />
          <div className={styles.content}>
            {winnersStories.data.stories_tiles.map((tile, index) => (
              <StoryTile
                key={index}
                image={tile.image}
                heading={tile.heading}
                description={tile.description}
                link={tile.link}
              />
            ))}
          </div>
        </section>
      )}

      {latestPosts.visibility.root && (
        <section
          className={styles.latestPosts}
          style={{
            backgroundColor: latestPosts.data.background_color
          }}
        >
          <HeadingWithAccentedText className={styles.heading} {...latestPosts.data.heading} />
          <div className={styles.content}>
            {latestPosts.data.posts_tiles.map((tile, index) => (
              <Tile
                key={index}
                image={tile.image}
                heading={tile.heading}
                subheading={tile.posted_by}
                description={tile.time_to_read}
                text={tile.description}
                cta={tile.cta}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Overview;
